import { useState, useCallback, useRef } from 'react';
import type {
  PlanningStrategy,
  OrchestratedStatusDict,
  ResearchStatusDict,
} from '../types/planning';

type PlanStatus = OrchestratedStatusDict | ResearchStatusDict | null;

interface UsePlanStatusReturn {
  strategy: PlanningStrategy | null;
  status: PlanStatus;
  isActive: boolean;
  isCollapsed: boolean;
  setStatus: (
    strategy: PlanningStrategy,
    status: PlanStatus,
    eventType?: string,
  ) => void;
  clearStatus: () => void;
  toggleCollapsed: () => void;
}

/**
 * Hook for managing plan status state from SSE events.
 *
 * Plan data comes exclusively from SSE `data-planStatus` events.
 * No REST API persistence - state is ephemeral per session.
 */
export function usePlanStatus(): UsePlanStatusReturn {
  const [strategy, setStrategy] = useState<PlanningStrategy | null>(null);
  const [status, setStatusState] = useState<PlanStatus>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const prevStrategyRef = useRef<PlanningStrategy | null>(null);

  const setStatus = useCallback(
    (
      newStrategy: PlanningStrategy,
      newStatus: PlanStatus,
      eventType?: string,
    ) => {
      // Auto-expand when strategy changes or plan is created
      if (prevStrategyRef.current !== newStrategy) {
        setIsCollapsed(false);
      }
      if (eventType === 'plan_create') {
        setIsCollapsed(false);
      } else if (eventType === 'plan_complete') {
        setIsCollapsed(true);
      }
      prevStrategyRef.current = newStrategy;

      setStrategy(newStrategy);
      setStatusState(newStatus);
    },
    [],
  );

  const clearStatus = useCallback(() => {
    setStrategy(null);
    setStatusState(null);
    setIsCollapsed(false);
    prevStrategyRef.current = null;
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  return {
    strategy,
    status,
    isActive: strategy !== null && status !== null,
    isCollapsed,
    setStatus,
    clearStatus,
    toggleCollapsed,
  };
}

/**
 * Extract plan status from SSE event data.
 * Accepts PlanStatusEvent from useChat which uses snake_case event_type.
 */
export function extractPlanStatusFromEvent(event: {
  event_type?: string;
  eventType?: string;
  strategy: PlanningStrategy;
  status: Record<string, unknown>;
}): {
  strategy: PlanningStrategy;
  status: PlanStatus;
  eventType?: string;
} | null {
  if (!event.strategy || !event.status) return null;

  return {
    strategy: event.strategy,
    status: event.status as unknown as PlanStatus,
    eventType: event.eventType ?? event.event_type,
  };
}
